import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { useServiceContext } from '../root'
import { ApiError } from '../services/api/base'
import { FileDetail, PathItem, FavoritesParams, getFileDetail, removeFileFromFavorite } from '../services/api/files'

export function useFilePath(fileId: number, enabled = true) {
  const { api } = useServiceContext()

  return useQuery({
    queryKey: ['filePath', fileId],
    queryFn: () => api.files.getFilePath(fileId),
    enabled: enabled && !!fileId
  })
}

export function useFileDetail(fileId: number, enabled = true) {
  const { api } = useServiceContext()

  return useQuery({
    queryKey: ['fileDetail', fileId],
    queryFn: () => api.files.getFileDetail(fileId),
    enabled: enabled && !!fileId
  })
}

export function useFile(fileId: number, enabled = true) {
  const { api } = useServiceContext()

  return useQuery({
    queryKey: ['file', fileId],
    queryFn: () => api.files.getFile(fileId),
    enabled: enabled && !!fileId
  })
}

export function useFileNavigation() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isNavigating, setIsNavigating] = useState(false)
  const { api, service } = useServiceContext()

  const navigateToFile = async (fileId: number, navigationPath?: string) => {
    setIsNavigating(true)

    try {
      await Promise.all([
        queryClient.fetchQuery({
          queryKey: ['fileDetail', fileId],
          queryFn: () => api.files.getFileDetail(fileId)
        }),
        queryClient.fetchQuery({
          queryKey: ['filePath', fileId],
          queryFn: () => api.files.getFilePath(fileId)
        })
      ])

      if (navigationPath) {
        navigate(navigationPath)
      } else {
        navigate(`/show-file/${fileId}`)
      }
    } catch (error: unknown) {
      if (error instanceof ApiError && [401, 403].includes(error.status)) {
        service.toast.warning(t('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'))
      } else {
        console.error('File navigation error:', error)
        service.toast.warning(t('GENERATING_VIEWABLE_DOC_NOT_VALID_MSG'))
      }
    } finally {
      setIsNavigating(false)
    }
  }

  return { navigateToFile, isNavigating }
}

interface UseFavoritesParams {
  params: {
    pageSize: number
    pageStart: number
    sortBy: string
    sortDesc: boolean
    name?: string
  }
}

export function useFavorites({ params }: UseFavoritesParams) {
  const { api } = useServiceContext()
  const queryKey = ['favorites', params.pageSize, params.pageStart, params.sortBy, params.sortDesc, params.name || '']

  return useQuery({
    queryKey,
    queryFn: () => {
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([, value]) => value !== undefined)
      ) as FavoritesParams
      return api.files.getFavorites(cleanParams)
    },
    enabled: true
  })
}

/**
 * React Query hook for fetching file detail
 */
export function useFileDetailQuery(fileId: number, enabled = true) {
  return useQuery({
    queryKey: ['fileDetail', fileId],
    queryFn: () => getFileDetail(fileId),
    enabled: enabled && !!fileId
  })
}

/**
 * React Query mutation for removing a file from favorites, invalidates file detail on success
 */
export function useRemoveFileFromFavoriteMutation(onSuccessCallback?: (fileId: number) => void) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (fileId: number) => removeFileFromFavorite(fileId),
    onSuccess: (_data, fileId) => {
      queryClient.invalidateQueries({ queryKey: ['fileDetail', 'favorites', 'file', 'filePath'] })
      if (onSuccessCallback) {
        onSuccessCallback(fileId)
      }
    }
  })
}

export type { FileDetail, PathItem, FavoritesParams }
